import { format } from "date-fns";
import { ChevronRight } from "lucide-react";
import React from "react";

import {
  Badge,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { TCampaign, TProjectOption } from "../../campaigns.types";
import {
  getCampaignChannelBadgeClass,
  getCampaignStatusBadgeClass,
} from "../../campaigns.utils";

interface ICampaignsTableProps {
  campaigns: TCampaign[];
  projects: TProjectOption[];
  isLoading: boolean;
  onViewDetails: (id: string) => void;
}

export const CampaignsTable: React.FC<ICampaignsTableProps> = ({
  campaigns,
  projects,
  isLoading,
  onViewDetails,
}) => {
  const { t } = useI18n();

  const projectName = React.useCallback(
    (projectId: string) =>
      projects.find((p) => p.value === projectId)?.label ?? projectId,
    [projects]
  );

  const planNames = React.useCallback((campaign: TCampaign) => {
    if (campaign.plans?.length) {
      return campaign.plans.map((p) => p.name).join(", ");
    }
    return campaign.plan_ids?.length ? campaign.plan_ids.join(", ") : "-";
  }, []);

  if (isLoading) {
    return (
      <Card className="p-4">
        <CardContent>
          <p className="text-muted-foreground">{t("generic.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="p-4 shadow-sm">
      <CardContent>
        {campaigns.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">{t("campaigns.no_campaigns")}</p>
          </div>
        )}

        {campaigns.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("campaigns.table.name")}</TableHead>
                <TableHead>{t("campaigns.table.project")}</TableHead>
                <TableHead>{t("campaigns.table.plan")}</TableHead>
                <TableHead>{t("campaigns.table.channel")}</TableHead>
                <TableHead>{t("campaigns.table.status")}</TableHead>
                <TableHead>{t("generic.created_at")}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors group"
                  onClick={() => onViewDetails(campaign.id)}
                >
                  <TableCell className="font-medium">{campaign.name}</TableCell>
                  <TableCell>{projectName(campaign.project_id)}</TableCell>
                  <TableCell className="max-w-[280px] truncate">
                    {planNames(campaign)}
                  </TableCell>
                  <TableCell>
                    <Badge className={getCampaignChannelBadgeClass(campaign.channel)}>
                      {t(`campaigns.channel.${campaign.channel}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getCampaignStatusBadgeClass(campaign.status)}>
                      {t(`campaigns.status.${campaign.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {format(new Date(campaign.created_at), "dd/MM/yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end">
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
